<script setup lang="ts">
import type { HTMLStyleElementWrapper } from '@@/bits/css'
import { appendCssToDocumentHead } from '@@/bits/css'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { tailwindSurfaceFontClass } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import { $ } from '@@/bits/jquery'
import { NON_STREAM_STANDARD_MAX_POST_SIZE, NON_STREAM_WIDE_MAX_POST_SIZE } from '@@/bits/post_size'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { WallWishSortByTypes } from '@@/enums'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceTimelineFormatStore } from '@@/pinia/surface_timeline_format_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { useAutoScrollElement } from '@@/pragmatic-drag-and-drop/useAutoScrollElement'
import type { Id, WallGroupPost } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import SurfacePostsTimeline from '@@/vuecomponents/SurfacePostsTimeline.vue'
import { TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE } from '@@/vuecomponents/SurfacePostsTimelineSectionHeader.vue'
import { TIMELINE_POST_DRAGGABLE_TYPE } from '@@/vuecomponents/SurfacePostTimeline.vue'
import SurfaceTimelineDashLine from '@@/vuecomponents/SurfaceTimelineDashLine.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { useScrollToSection } from '@@/vuecomposables/surface_scroll_to_section'
import { useResizeObserver } from '@vueuse/core'
import { throttle } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import tinykeys from 'tinykeys'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

useDoubleClickToPost()

const {
  xHeader,
  backgroundLuminance,
  isReadOnly,
  isScreenshotMode,
  isMobileLayout,
  isPostSizeWide,
  colorScheme,
  isSectioned,
  fontId,
  isOverlayVisible,
} = storeToRefs(useSurfaceStore())
const { canIWrite } = storeToRefs(useSurfacePermissionsStore())
const surfacePostsStore = useSurfacePostsStore()
const { postBeingEditedCid, allSortedPosts } = storeToRefs(surfacePostsStore)
const { isAPostDragging } = storeToRefs(useSurfacePostsDragAndDropStore())
const { sortedGroups, originalWishSortBy, isGroupByAuthor } = storeToRefs(useSurfaceWishArrangementStore())
const { isAGroupDragging } = storeToRefs(useSurfacePostGroupByStore())
const surfacePostSearchStore = useSurfacePostSearchStore()
const surfaceFilterStore = useSurfaceFilterStore()
const windowSizeStore = useWindowSizeStore()
const { dashedLineMarginPercentFromViewportBottom, dashedLineMarginBottomClasses } = storeToRefs(
  useSurfaceTimelineFormatStore(),
)

// 430px is iPhone 14 Pro Max height in landscape.
const isMobileLandscapeScreen = computed(() => windowSizeStore.windowHeight <= 430)

const canShowAddPostButton = computed<boolean>(() => {
  return (
    isReadOnly.value === false &&
    canIWrite.value &&
    !isAPostDragging.value &&
    (originalWishSortBy.value === WallWishSortByTypes.ManualNewPostsFirst ||
      originalWishSortBy.value === WallWishSortByTypes.ManualNewPostsLast) &&
    !isGroupByAuthor.value
  )
})

const wishList = ref<HTMLElement | null>(null)

// #region Start/end marks
const startOfLine = ref<HTMLElement>()
const endOfLine = ref<HTMLElement>()
const isAtStart = ref(true)
const isAtEnd = ref(false)
let startIntersectionObserver: IntersectionObserver | null = null
let endIntersectionObserver: IntersectionObserver | null = null

const observeIntersection = (): void => {
  if (startOfLine.value != null) {
    startIntersectionObserver = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio === 0) {
        isAtStart.value = false
      } else {
        isAtStart.value = true
      }
    })
    startIntersectionObserver.observe(startOfLine.value)
  }

  if (endOfLine.value != null) {
    endIntersectionObserver = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio === 0) {
        isAtEnd.value = false
      } else {
        isAtEnd.value = true
      }
    })
    endIntersectionObserver.observe(endOfLine.value)
  }
}
// #endregion

// #region Scroll to start/left/right/end/section
const currentSectionIndex = ref<number>(0)

const scrollToStart = (): void => {
  if (wishList.value == null) return
  $(wishList.value).animate({ scrollLeft: 0 }, '350')
}

const scrollHorizontally = (direction: 'left' | 'right'): void => {
  if (wishList.value == null) return
  const currentScroll = wishList.value.scrollLeft
  const viewportWidth = document.body.offsetWidth
  const scrollAmount = viewportWidth * 0.45

  const isRtl = dir() === 'rtl'
  const multiplier = (isRtl && direction === 'left') || (!isRtl && direction === 'right') ? 1 : -1
  const newScroll = currentScroll + scrollAmount * multiplier

  wishList.value.scrollTo({
    left: newScroll,
    behavior: 'smooth',
  })
}

const scrollHorizontallyWithKeyboard = (key: 'arrowLeft' | 'arrowRight'): void => {
  if (wishList.value == null) return
  wishList.value.scrollTo({
    left: wishList.value.scrollLeft + (key === 'arrowRight' ? 50 : -50),
  })
}

const scrollToEnd = (): void => {
  if (wishList.value == null) return
  let scrollLeft = wishList.value.scrollWidth
  if (dir() === 'rtl') scrollLeft *= -1
  wishList.value.scrollTo({
    left: scrollLeft,
    behavior: 'smooth',
  })
}

/**
 * Checks if the current section is visible in the viewport.
 * Returns true if the section title is fully visible in the viewport.
 */
const isCurrentSectionFullyVisible = (): boolean => {
  if (wishList.value == null || currentSectionIndex.value >= sortedGroups.value.length) return false

  const sectionElement = sectionElements.value.get(String(sortedGroups.value[currentSectionIndex.value].id))
  if (!sectionElement) return false

  const scrollLeft = wishList.value.scrollLeft
  const viewportLeft = scrollLeft
  const viewportRight = scrollLeft + wishList.value.clientWidth
  const sectionOffsetLeft = sectionElement.offsetLeft
  const sectionOffsetRight = sectionOffsetLeft + sectionElement.offsetWidth

  return dir() === 'ltr' ? sectionOffsetLeft >= viewportLeft : sectionOffsetRight <= viewportRight
}

const scrollToPreviousSection = (): void => {
  if (currentSectionIndex.value === 0) return

  const targetIndex = isCurrentSectionFullyVisible()
    ? currentSectionIndex.value - 1 // If current section is visible, go to previous
    : currentSectionIndex.value // If current section is not visible, go to current

  scrollToSection(Number(sortedGroups.value[targetIndex].id), { smooth: true })
  currentSectionIndex.value = targetIndex
}

const scrollToNextSection = (): void => {
  if (currentSectionIndex.value === sortedGroups.value.length - 1) return
  scrollToSection(Number(sortedGroups.value[currentSectionIndex.value + 1].id), { smooth: true })
  currentSectionIndex.value += 1
}

// Check if the keyboard event should be ignored (e.g., from input elements or modals)
const shouldIgnoreKeyboardEvent = (event: KeyboardEvent): boolean => {
  const target = event.target as HTMLElement | null
  if (target === null) return false

  if (isOverlayVisible.value) return true

  // Check if the target itself is an input element
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
    return true
  }

  // Check if the target has contenteditable attribute
  if (target.getAttribute('contenteditable') === 'true') {
    return true
  }

  // Check if the target is inside a modal, dialog, or post composer
  const isInModal = target.closest('[role="dialog"], [role="modal"], .modal-container')
  if (isInModal !== null) {
    return true
  }

  // Check if the target is inside a search bar or filter modal
  const isInSearchOrFilter = target.closest('#surface-search-bar')
  if (isInSearchOrFilter !== null) {
    return true
  }

  return false
}
// #endregion

// #region - Auto scroll when dragging section header
useAutoScrollElement({
  scrollContainerElementRef: wishList,
  type: [TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE, TIMELINE_POST_DRAGGABLE_TYPE],
})
// #endregion

// #region Wish max height
const surfaceHeaderRef = ref<InstanceType<typeof SurfaceHeader>>()

let maxHeightForRealWishStyle: HTMLStyleElementWrapper | null = null

const getWishListMarginBottom = (): number => {
  // mb-6.5
  if (isMobileLandscapeScreen.value) return 26
  // mb-36
  if (isMobileLayout.value) return 144
  // mb-54
  if (device.app) return 216

  return windowSizeStore.windowHeight * dashedLineMarginPercentFromViewportBottom.value
}

const isScrollbarVisible = () => {
  const timelinePostsEl = timelinePostsRef.value?.$el
  const wallContainerEl = wallContainerRef.value
  if (!timelinePostsEl || !wallContainerEl) {
    return false
  }
  return timelinePostsEl.scrollWidth > wallContainerEl.scrollWidth
}

const updateWishMaxHeight = (surfaceHeaderHeight: number): void => {
  // Set 56px gap between surface header and post only for desktop web layout. Mobile layout and mobile app have no gap
  const distanceFromSurfaceHeaderToPost = isMobileLayout.value || device.app || isMobileLandscapeScreen.value ? 0 : 56
  // 19px gap between post and line + 22px dash line height
  const distanceFromPostToLine = 41
  const wishListMarginBottom = getWishListMarginBottom()
  const bottomBarHeight = isMobileLayout.value ? 56 : 0
  const approvalMessageHeight = 40
  const pinnedWishHeight = 36
  const scrollbarHeight = isScrollbarVisible() ? 14 : 0
  // when a wish requires approval (.unapproved-wish), we need an extra 40px space for the approval UI
  const css = `
      .timeline-wish {
        max-height: calc(var(--vh100) - ${surfaceHeaderHeight}px - ${distanceFromSurfaceHeaderToPost}px - ${distanceFromPostToLine}px - ${wishListMarginBottom}px - ${scrollbarHeight}px - ${bottomBarHeight}px);
      }
      .timeline-wish.unapproved-wish {
        max-height: calc(var(--vh100) - ${surfaceHeaderHeight}px - ${distanceFromSurfaceHeaderToPost}px - ${distanceFromPostToLine}px - ${wishListMarginBottom}px - ${approvalMessageHeight}px - ${scrollbarHeight}px - ${bottomBarHeight}px);
      }
      .timeline-wish.pinned-wish {
        max-height: calc(var(--vh100) - ${surfaceHeaderHeight}px - ${distanceFromSurfaceHeaderToPost}px - ${distanceFromPostToLine}px - ${wishListMarginBottom}px - ${pinnedWishHeight}px - ${scrollbarHeight}px - ${bottomBarHeight}px);
      }
      `
  maxHeightForRealWishStyle?.remove()
  maxHeightForRealWishStyle = appendCssToDocumentHead(css)
}

useResizeObserver(surfaceHeaderRef, (entries) => {
  updateWishMaxHeight(entries[0].contentRect.height)
})
// #endregion

// #region - End dashed line
const timelinePostsRef = ref<InstanceType<typeof SurfacePostsTimeline>>()
const wallContainerRef = ref<HTMLDivElement>()
const endDashLineWidth = ref(0)

const endDashLineCount = computed(() => {
  return Math.ceil(endDashLineWidth.value / 14) // 14 is the 12px dash line width + gap 2px between dashes
})

const hasCalculatedScreenshotDashLine = ref(false)

const updateEndDashLineWidth = (): void => {
  // Don't calculate dash line width twice in screenshot mode
  // This is to prevent infinite loop where the dash line is keep increasing
  if (hasCalculatedScreenshotDashLine.value) return
  if (isScreenshotMode.value) {
    hasCalculatedScreenshotDashLine.value = true
  }

  const timelinePostsEl = timelinePostsRef.value?.$el
  const wallContainerEl = wallContainerRef.value
  if (!timelinePostsEl || !wallContainerEl) {
    endDashLineWidth.value = 0
    return
  }
  if (timelinePostsEl.scrollWidth > wallContainerEl.scrollWidth) {
    endDashLineWidth.value = 0
    return
  }
  endDashLineWidth.value = wallContainerEl.scrollWidth - timelinePostsEl.scrollWidth
}

// This is to fix issue where the dashed lines are too wide in screenshot mode.
// The issue is because sortedGroups and allSortedPosts is empty onMounted
// so we need to wait for the sortedGroups and allSortedPosts to be set and
// then calculate the dash line width.
if (isScreenshotMode.value) {
  watch([sortedGroups, allSortedPosts], () => {
    endDashLineWidth.value = 0
    setTimeout(() => {
      hasCalculatedScreenshotDashLine.value = false
      updateEndDashLineWidth()
    }, 0)
  })
}

useResizeObserver(wallContainerRef, updateEndDashLineWidth)
useResizeObserver(timelinePostsRef, updateEndDashLineWidth)
// #endregion

// #region - Previous section header button
const { scrollToSection } = useScrollToSection()

// Store section header DOM element references
const sectionElements = ref<Map<string, HTMLElement>>(new Map())

const nearestPreviousSection = ref<Group | null>(null)

const twFontClass = computed(() => tailwindSurfaceFontClass(fontId.value))

const xNearestPreviousSection = computed<boolean>(() => {
  if (!isSectioned.value) return false
  if (isScreenshotMode.value) return false
  if (isAGroupDragging.value) return false
  if (nearestPreviousSection.value == null || wishList.value == null) return false
  if (surfacePostSearchStore.searchTerm !== '' || surfaceFilterStore.hasSelectedFilters) return false

  const sectionElement = sectionElements.value.get(String(nearestPreviousSection.value.id))
  if (sectionElement == null) return false

  const sectionHeader = sectionElement.querySelector('header')
  if (!sectionHeader) return false

  const sectionRect = sectionHeader.getBoundingClientRect()
  const containerRect = wishList.value.getBoundingClientRect()

  // Return true if the section is completely outside the viewport
  return sectionRect.right <= containerRect.left || sectionRect.left >= containerRect.right
})

/**
 * Updates the nearest section to the left of the current scroll position.
 * This is used to show a "jump to section" button when scrolling horizontally.
 * The method:
 * 1. Gets all section groups and their DOM positions (offsetLeft)
 * 2. Finds the rightmost section among those to the left (nearest to current view)
 * The result is throttled to prevent excessive updates while scrolling.
 */
const updateNearestPreviousSection = throttle((): void => {
  if (!isSectioned.value) return

  const groups = sortedGroups.value
  if (groups.length === 0 || wishList.value == null) return

  const scrollLeft = wishList.value.scrollLeft
  const viewportLeft = scrollLeft
  const viewportRight = scrollLeft + wishList.value.clientWidth

  const groupsWithOffsets = groups
    .map((group) => {
      const element = sectionElements.value.get(String(group.id))
      if (!element) return null
      const offsetLeft = element.offsetLeft
      const offsetRight = offsetLeft + element.offsetWidth
      return { ...group, offsetLeft, offsetRight }
    })
    .filter((group): group is WallGroupPost & { offsetLeft: number; offsetRight: number } => group !== null)

  if (groupsWithOffsets.length === 0) return

  if (dir() === 'ltr') {
    const candidates = groupsWithOffsets.filter(
      (group) => group.offsetLeft <= viewportLeft && group.offsetRight >= viewportLeft,
    )
    if (candidates.length > 0) {
      const nearestLtr = candidates.reduce((prev, current) => (current.offsetLeft > prev.offsetLeft ? current : prev))
      const nearestIndexLtr = groupsWithOffsets.indexOf(nearestLtr)
      nearestPreviousSection.value = nearestLtr as Group
      currentSectionIndex.value = nearestIndexLtr
    }
  } else {
    const candidates = groupsWithOffsets.filter(
      (group) => group.offsetRight >= viewportRight && group.offsetLeft <= viewportRight,
    )
    if (candidates.length > 0) {
      const nearestRtl = candidates.reduce((prev, current) => (current.offsetRight < prev.offsetRight ? current : prev))
      const nearestIndexRtl = groupsWithOffsets.indexOf(nearestRtl)
      nearestPreviousSection.value = nearestRtl as Group
      currentSectionIndex.value = nearestIndexRtl
    }
  }
}, 100)

/**
 * Builds a map of section IDs to their DOM elements for efficient lookup.
 * This cache is used by updateNearestPreviousSection to calculate section positions
 * without having to query the DOM repeatedly. The map is updated when sections
 * change or when sectioned mode is enabled.
 */
const updateSectionElements = (): void => {
  sortedGroups.value?.forEach((group) => {
    const element = document.querySelector(`#section-${group.id}`)
    if (element) sectionElements.value.set(String(group.id), element as HTMLElement)
  })
}

watch(
  sortedGroups,
  () => {
    nextTick(() => {
      updateSectionElements()
      updateNearestPreviousSection()
    })
  },
  { immediate: true },
)
// #endregion

watch(
  isPostSizeWide,
  (newIsPostSizeWide) => {
    document.documentElement.style.setProperty(
      '--post-width',
      newIsPostSizeWide ? `${NON_STREAM_WIDE_MAX_POST_SIZE}px` : `${NON_STREAM_STANDARD_MAX_POST_SIZE}px`,
    )
    document.documentElement.style.setProperty(
      '--distance-between-posts',
      newIsPostSizeWide ? `${NON_STREAM_WIDE_MAX_POST_SIZE}px` : `${NON_STREAM_STANDARD_MAX_POST_SIZE}px`,
    )
  },
  { immediate: true },
)

watch(
  () => [windowSizeStore.windowHeight, dashedLineMarginPercentFromViewportBottom.value],
  () => {
    // Recalculate the max height of the wish because wish max height is calculated based on the window height
    updateWishMaxHeight((surfaceHeaderRef.value?.$el as HTMLElement)?.offsetHeight ?? 0)

    if (!isMobileLandscapeScreen.value) return
    nextTick(() => {
      // Auto-collapse surface header in landscape orientation.
      surfaceHeaderRef.value?.collapse()
    })
  },
  { immediate: true },
)

let removeKeyboardShortcutsListener = (): void => {}

// #region Life cycle
onMounted(() => {
  nextTick(() => {
    observeIntersection()
    updateEndDashLineWidth()
    updateWishMaxHeight((surfaceHeaderRef.value?.$el as HTMLElement)?.offsetHeight ?? 0)

    wishList.value?.addEventListener('scroll', updateNearestPreviousSection)
  })

  if (isScreenshotMode.value) {
    const wallContainer = wallContainerRef.value
    if (!wallContainer) return
    wallContainer.style.backgroundSize = 'cover'
    wallContainer.style.backgroundImage = document.body.style.backgroundImage
    document.body.style.backgroundImage = ''
  }

  // Add keyboard shortcuts
  if (wishList.value) {
    removeKeyboardShortcutsListener = tinykeys(window, {
      ArrowLeft: (event) => {
        if (shouldIgnoreKeyboardEvent(event)) return
        scrollHorizontallyWithKeyboard('arrowLeft')
      },
      ArrowRight: (event) => {
        if (shouldIgnoreKeyboardEvent(event)) return
        scrollHorizontallyWithKeyboard('arrowRight')
      },
      '$mod+ArrowLeft': (event) => {
        if (shouldIgnoreKeyboardEvent(event)) return
        if (dir() === 'rtl') {
          scrollToEnd()
        } else {
          scrollToStart()
        }
        // Prevent the browser from navigating back to the previous page
        event.preventDefault()
      },
      '$mod+ArrowRight': (event) => {
        if (shouldIgnoreKeyboardEvent(event)) return
        if (dir() === 'rtl') {
          scrollToStart()
        } else {
          scrollToEnd()
        }
      },
      'Shift+ArrowLeft': (event) => {
        if (shouldIgnoreKeyboardEvent(event)) return
        if (!isSectioned.value) return

        if (dir() === 'rtl') {
          scrollToNextSection()
        } else {
          scrollToPreviousSection()
        }
      },
      'Shift+ArrowRight': (event) => {
        if (shouldIgnoreKeyboardEvent(event)) return
        if (!isSectioned.value) return

        if (dir() === 'rtl') {
          scrollToPreviousSection()
        } else {
          scrollToNextSection()
        }
      },
    })
  }
})

onBeforeUnmount(() => {
  maxHeightForRealWishStyle?.remove()
  maxHeightForRealWishStyle = null

  startIntersectionObserver?.disconnect()
  startIntersectionObserver = null

  endIntersectionObserver?.disconnect()
  endIntersectionObserver = null

  wishList.value?.removeEventListener('scroll', updateNearestPreviousSection)
  removeKeyboardShortcutsListener()
})
// #endregion
</script>

<template>
  <div id="wall-container" ref="wallContainerRef" :class="!isScreenshotMode && 'overflow-hidden'">
    <SurfaceHeader v-if="xHeader" ref="surfaceHeaderRef" />
    <div
      id="wish-list"
      ref="wishList"
      :dir="dir()"
      :class="[
        'flex',
        !isScreenshotMode && 'overflow-auto',
        `background-luminance-${backgroundLuminance}`,
        {
          'scrollbar-photo': backgroundLuminance === 'light',
          'scrollbar-photo-dark': backgroundLuminance === 'dark',
        },
      ]"
      @scroll="$emit('scroll', $event)"
    >
      <div :class="['flex', endDashLineWidth > 0 && 'max-w-full overflow-hidden']">
        <span ref="startOfLine" />
        <SurfacePostsTimeline
          ref="timelinePostsRef"
          :post-being-edited-cid="postBeingEditedCid"
          :reaction-ids-by-post-id="reactionIdsByPostId"
          :comment-ids-by-post-id="commentIdsByPostId"
          :can-show-add-post-button="canShowAddPostButton"
          :is-mobile-landscape-screen="isMobileLandscapeScreen"
        />
        <span ref="endOfLine" />
        <div
          :class="[
            'overflow-hidden self-end flex items-end ps-0.5 gap-0.5',
            isMobileLandscapeScreen && ['mb-6.5'],
            !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
          ]"
          :style="{ width: `${endDashLineWidth}px` }"
        >
          <SurfaceTimelineDashLine
            v-for="n in endDashLineCount"
            :key="`long-end-dashed-line-${n}`"
            class="shrink-0 mb-[9.5px]"
            :dark-mode="backgroundLuminance === 'dark'"
          />
        </div>
        <OzBaseButton
          v-if="
            !isMobileLandscapeScreen &&
            (isMobileLayout || device.app) &&
            xNearestPreviousSection &&
            nearestPreviousSection != null
          "
          :aria-label="__('Scroll to %{sectionTitle}', { sectionTitle: nearestPreviousSection.title })"
          :class="[
            'fixed start-4 rounded-lg px-4 py-2 text-body-small max-w-[200px] truncate backdrop-blur-md focus-visible:ring-2',
            backgroundLuminance === 'light' && [
              // Base
              'text-dark-text-100 bg-[rgba(255,255,255,0.32)]',
              // Hover
              'hover-hover:hover:text-dark-text-200',
            ],
            backgroundLuminance === 'dark' && [
              // Base
              'text-light-text-100 bg-[rgba(0,0,0,0.24)]',
              // Hover
              'hover-hover:hover:text-light-text-200',
            ],
            colorScheme === 'light' && [
              // Focus
              'focus-visible:ring-grape-500',
              // Active
              'hover-hover:active:text-grape-500',
            ],
            colorScheme === 'dark' && [
              // Focus
              'focus-visible:ring-canary-500',
              // Active
              'hover-hover:active:text-canary-500',
            ],
            isMobileLayout && 'bottom-36', // Mobile web
            !isMobileLayout && [
              device.app && 'bottom-22 @tablet-portrait/surface-container:bottom-[167px]', // Mobile app
              !device.app && 'bottom-[167px]', // Desktop web
            ],
          ]"
          :font-class="twFontClass"
          @click="scrollToSection(Number(nearestPreviousSection.id), { smooth: true })"
        >
          {{ nearestPreviousSection.title }}
        </OzBaseButton>
        <OzBox
          v-if="!isScreenshotMode"
          :class="[
            'fixed start-4 rounded-xl flex gap-1 backdrop-blur-12',
            backgroundLuminance === 'dark' ? 'bg-[rgba(0,0,0,0.24)]' : 'bg-[rgba(255,255,255,0.32)]',
            isAtStart && '-translate-y-1/2 scale-0',
            isMobileLandscapeScreen && 'z-20',
            isMobileLayout && 'bottom-22', // Mobile web
            !isMobileLayout && [
              device.app && 'bottom-8 @tablet-portrait/surface-container:bottom-30', // Mobile app
              // Desktop web - The UI should be 40px gap from `Add section` button
              !device.app && [
                'px-1.5',
                dashedLineMarginPercentFromViewportBottom === 0.25 &&
                  '@tablet-portrait/surface-container:bottom-[calc(100vh*0.25-62px)]',
                dashedLineMarginPercentFromViewportBottom === 0.36 &&
                  '@tablet-portrait/surface-container:bottom-[calc(100vh*0.36-62px)]',
              ],
            ],
          ]"
          :color="OzBoxColors.None"
          :dark-mode="colorScheme === 'dark'"
          :radius="12"
        >
          <OzBaseButton
            :class="[
              'w-8 h-8 flex items-center justify-center rounded-xl',
              'focus:outline-none',
              'bg-transparent',
              'focus-visible:ring-2',
              backgroundLuminance === 'dark'
                ? 'text-light-text-200 hover-hover:hover:text-light-text-100'
                : 'text-dark-text-200 hover-hover:hover:text-dark-text-100',
              colorScheme === 'light' && [
                'hover-hover:active:text-grape-500',
                'focus-visible:text-grape-500',
                'focus-visible:ring-grape-500',
              ],
              colorScheme === 'dark' && [
                'hover-hover:active:text-canary-500',
                'focus-visible:text-canary-500',
                'focus-visible:ring-canary-500',
              ],
            ]"
            :aria-label="__('Scroll to the first post in the padlet')"
            @click="scrollToStart"
          >
            <OzIcon name="skip_backward" :size="20" />
          </OzBaseButton>
          <OzBaseButton
            v-if="!isMobileLayout && !device.app"
            :class="[
              'w-8 h-8 flex items-center justify-center rounded-xl',
              'focus:outline-none',
              'bg-transparent',
              'focus-visible:ring-2',
              backgroundLuminance === 'dark'
                ? 'text-light-text-200 hover-hover:hover:text-light-text-100'
                : 'text-dark-text-200 hover-hover:hover:text-dark-text-100',
              colorScheme === 'light' && [
                'hover-hover:active:text-grape-500',
                'focus-visible:text-grape-500',
                'focus-visible:ring-grape-500',
              ],
              colorScheme === 'dark' && [
                'hover-hover:active:text-canary-500',
                'focus-visible:text-canary-500',
                'focus-visible:ring-canary-500',
              ],
            ]"
            :aria-label="__('Scroll to the previous view in the padlet')"
            @click="scrollHorizontally('left')"
          >
            <OzIcon name="chevron_left_center" :size="20" />
          </OzBaseButton>
        </OzBox>
        <OzBox
          v-if="!isScreenshotMode"
          :class="[
            'fixed rounded-xl flex gap-1 backdrop-blur-12',
            backgroundLuminance === 'dark' ? 'bg-[rgba(0,0,0,0.24)]' : 'bg-[rgba(255,255,255,0.32)]',
            isAtEnd && '-translate-y-1/2 scale-0',
            isMobileLandscapeScreen && 'z-20',
            isMobileLayout && 'bottom-22 end-4', // Mobile web
            !isMobileLayout && [
              device.app && 'bottom-8 @tablet-portrait/surface-container:bottom-30 end-4', // Mobile app
              // Desktop web - The UI should be 40px gap from `Add section` button
              !device.app && [
                'end-22 px-1.5',
                dashedLineMarginPercentFromViewportBottom === 0.25 &&
                  '@tablet-portrait/surface-container:bottom-[calc(100vh*0.25-62px)]',
                dashedLineMarginPercentFromViewportBottom === 0.36 &&
                  '@tablet-portrait/surface-container:bottom-[calc(100vh*0.36-62px)]',
              ],
            ],
          ]"
          :color="OzBoxColors.None"
          :dark-mode="colorScheme === 'dark'"
          :radius="12"
        >
          <OzBaseButton
            v-if="!isMobileLayout && !device.app"
            :class="[
              'w-8 h-8 flex items-center justify-center rounded-xl',
              'focus:outline-none',
              'bg-transparent',
              'focus-visible:ring-2',
              backgroundLuminance === 'dark'
                ? 'text-light-text-200 hover-hover:hover:text-light-text-100'
                : 'text-dark-text-200 hover-hover:hover:text-dark-text-100',
              colorScheme === 'light' && [
                'hover-hover:active:text-grape-500',
                'focus-visible:text-grape-500',
                'focus-visible:ring-grape-500',
              ],
              colorScheme === 'dark' && [
                'hover-hover:active:text-canary-500',
                'focus-visible:text-canary-500',
                'focus-visible:ring-canary-500',
              ],
            ]"
            :aria-label="__('Scroll to the next view in the padlet')"
            @click="scrollHorizontally('right')"
          >
            <OzIcon name="chevron_right_center" :size="20" />
          </OzBaseButton>
          <OzBaseButton
            :class="[
              'w-8 h-8 flex items-center justify-center rounded-xl',
              'focus:outline-none',
              'bg-transparent',
              'focus-visible:ring-2',
              backgroundLuminance === 'dark'
                ? 'text-light-text-200 hover-hover:hover:text-light-text-100'
                : 'text-dark-text-200 hover-hover:hover:text-dark-text-100',
              colorScheme === 'light' && [
                'hover-hover:active:text-grape-500',
                'focus-visible:text-grape-500',
                'focus-visible:ring-grape-500',
              ],
              colorScheme === 'dark' && [
                'hover-hover:active:text-canary-500',
                'focus-visible:text-canary-500',
                'focus-visible:ring-canary-500',
              ],
            ]"
            :aria-label="__('Scroll to the last post in the padlet')"
            @click="scrollToEnd"
          >
            <OzIcon name="skip_forward" :size="20" />
          </OzBaseButton>
        </OzBox>
      </div>
    </div>
  </div>
</template>

<style>
html.screenshot .timeline-wish {
  max-height: unset !important;
}
</style>
